import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Tiny live-status indicator. Polls /api/health once on mount.
 *  - green dot + "Live · N vacancies indexed" when db_ok && vacancies_live > 0
 *  - amber dot + "Status: degraded" when ok but no vacancies / db_ok=false
 *  - grey dot + "Checking…" while loading or on fetch error
 *
 * Intentionally non-blocking — never throws, never blocks render.
 */
export default function SiteHealthPill() {
  const [state, setState] = useState({ loading: true, ok: false, dbOk: false, n: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/health");
        if (!alive) return;
        setState({
          loading: false,
          ok: !!r.data?.ok,
          dbOk: !!r.data?.db_ok,
          n: r.data?.vacancies_live || 0,
        });
      } catch {
        if (!alive) return;
        setState({ loading: false, ok: false, dbOk: false, n: 0 });
      }
    })();
    return () => { alive = false; };
  }, []);

  let dotCls = "bg-stone-400";
  let label = "Checking status…";
  if (!state.loading) {
    if (state.ok && state.dbOk && state.n > 0) {
      dotCls = "bg-emerald-500";
      label = `Live · ${state.n.toLocaleString("en-GB")} vacancies indexed`;
    } else if (state.ok) {
      dotCls = "bg-amber-400";
      label = "Status: degraded";
    } else {
      dotCls = "bg-rose-500";
      label = "Status: offline";
    }
  }

  return (
    <div
      className="inline-flex items-center gap-2 text-[12px] text-slate"
      data-testid="site-health-pill"
      title={label}
    >
      <span className={`relative inline-flex w-2 h-2 rounded-full ${dotCls}`}>
        {state.ok && state.dbOk && (
          <span className={`absolute inset-0 rounded-full ${dotCls} opacity-60 animate-ping`} />
        )}
      </span>
      <span data-testid="site-health-label">{label}</span>
    </div>
  );
}
